import React, { useState, useEffect } from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { peerCountries } from '../../../data/peerData';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { usePanelLink } from '../../../hooks/usePanelLink';

// @ts-ignore - Recharts types issue with React 19
const TypedPolarAngleAxis = PolarAngleAxis as any;
// @ts-ignore
const TypedPolarRadiusAxis = PolarRadiusAxis as any;
// @ts-ignore
const TypedPolarGrid = PolarGrid as any;

// Central Asia & Silk Road Corridor colors
const countryColors: Record<string, string> = {
  UZB: '#00d26a',  // Primary - Green
  KAZ: '#fbbf24',  // Kazakhstan - Amber
  KGZ: '#00a3ff',  // Kyrgyzstan - Blue
  TJK: '#00d4d4',  // Tajikistan - Cyan
  TKM: '#f97316',  // Turkmenistan - Orange
  AZE: '#ef4444',  // Azerbaijan - Red
  GEO: '#a855f7',  // Georgia - Purple
  MNG: '#6b7280'   // Mongolia - Gray
};

interface RadarComparisonPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const RadarComparisonPanel: React.FC<RadarComparisonPanelProps> = ({ isMinimized, onMinimize }) => {
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['UZB', 'KAZ', 'GEO', 'AZE']);
  const { selectedEntity, isLinked } = usePanelLink('radar-comparison');

  // When a linked panel selects a country, include it in selected countries
  useEffect(() => {
    if (isLinked && selectedEntity?.type === 'country') {
      const countryCode = selectedEntity.id;
      if (countryColors[countryCode]) {
        setSelectedCountries(prev =>
          prev.includes(countryCode) ? prev : [...prev, countryCode]
        );
      }
    }
  }, [selectedEntity, isLinked]);

  // Get linked country for enhanced styling
  const linkedCountry = isLinked && selectedEntity?.type === 'country' ? selectedEntity.id : null;

  const toggleCountry = (code: string) => {
    if (code === 'UZB') return; // Always show UZB
    setSelectedCountries(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const radarData = [
    { metric: 'Market Access', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.marketAccess])) },
    { metric: 'Stability', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.stability])) },
    { metric: 'Growth', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.growth])) },
    { metric: 'Governance', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.governance])) },
    { metric: 'Infrastructure', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.infrastructure])) },
    { metric: 'Talent', fullMark: 100, ...Object.fromEntries(peerCountries.map(c => [c.code, c.talent])) },
  ];

  const uzb = peerCountries.find(c => c.code === 'UZB')!;
  const avgScore = (c: typeof uzb) =>
    ((c.marketAccess + c.stability + c.growth + c.governance + c.infrastructure + c.talent) / 6).toFixed(0);

  return (
    <Panel id="radar-comparison" title="RADAR COMPARISON" subtitle="Multi-Dimensional Analysis" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full">
        {/* Country Selector */}
        <div className="flex flex-wrap gap-1 mb-2">
          {peerCountries.map(c => (
            <button
              key={c.code}
              onClick={() => toggleCountry(c.code)}
              className={`px-2 py-0.5 text-xs rounded transition-all ${
                selectedCountries.includes(c.code)
                  ? 'text-white font-medium'
                  : 'bg-terminal-bg text-gray-500 hover:text-gray-300'
              }`}
              style={{
                backgroundColor: selectedCountries.includes(c.code) ? `${countryColors[c.code]}33` : undefined,
                borderColor: selectedCountries.includes(c.code) ? countryColors[c.code] : 'transparent',
                borderWidth: '1px'
              }}
              disabled={c.code === 'UZB'}
            >
              {c.code}
              {c.code === 'UZB' && ' ★'}
            </button>
          ))}
        </div>

        {/* Radar Chart */}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
              <TypedPolarGrid stroke="#1e1e26" />
              <TypedPolarAngleAxis
                dataKey="metric"
                tick={{ fontSize: 8, fill: '#9ca3af' }}
              />
              <TypedPolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fontSize: 7, fill: '#6b7280' }}
                tickCount={5}
              />
              {selectedCountries.map(code => {
                const isLinkedCountry = code === linkedCountry;
                const isUzb = code === 'UZB';
                return (
                  <Radar
                    key={code}
                    name={code}
                    dataKey={code}
                    stroke={countryColors[code]}
                    fill={countryColors[code]}
                    fillOpacity={isLinkedCountry ? 0.35 : isUzb ? 0.25 : linkedCountry ? 0.05 : 0.1}
                    strokeWidth={isLinkedCountry ? 3 : isUzb ? 2 : 1}
                    strokeOpacity={linkedCountry && !isLinkedCountry ? 0.3 : 1}
                  />
                );
              })}
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '6px',
                  fontSize: '11px',
                  padding: '10px 14px',
                  boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '6px' }}
              />
              <Legend
                verticalAlign="bottom"
                height={24}
                formatter={(value: string) => (
                  <span style={{ color: countryColors[value], fontSize: '9px' }}>{value}</span>
                )}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Footer */}
        <div className="pt-2 border-t border-terminal-border flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default RadarComparisonPanel;
